/**
 * Utilidades para formateo de valores monetarios con formato brasileño
 * Formato: 1.234.567,89 (punto como separador de miles, coma como separador decimal)
 * 
 * PROBLEMA RESUELTO:
 * - Al pegar "1.550,26" se convertía incorrectamente a "1.55026"
 * - En base de datos llegaba como "155026" en lugar de "1550.26"
 * - Múltiples implementaciones inconsistentes en diferentes componentes
 * 
 * SOLUCIÓN:
 * - Detección inteligente de formato brasileño vs. americano
 * - Preservación de posición del cursor durante formateo
 * - Normalización automática para envío al backend
 * - Directiva Angular para uso fácil
 * 
 * USO BÁSICO:
 * ```typescript
 * // En componente TypeScript
 * import { CurrencyFormatter } from './utils/currency-formatter';
 * 
 * handleNumericInput(event: any): void {
 *   const input = event.target as HTMLInputElement;
 *   CurrencyFormatter.handleInput(input, { maxDecimals: 2, allowNegative: false });
 * }
 * 
 * normalizeValue(value: any): number {
 *   return CurrencyFormatter.formatForBackend(value);
 * }
 * ```
 * 
 * ```html
 * <!-- En template HTML -->
 * <input (input)="handleNumericInput($event)" 
 *        (blur)="handleNumericBlur($event)"
 *        (focus)="handleNumericFocus($event)"
 *        placeholder="0,00">
 * 
 * <!-- O usando directiva -->
 * <input currencyInput [currencyOptions]="{maxDecimals: 2}">
 * ```
 * 
 * FLUJO DE DATOS:
 * Usuario → "1.550,26" → Display: "1.550,26" → Backend: 1550.26 → DB: 1550.26
 * 
 * @author Sistema AutoComercial del Norte
 * @version 1.0
 * @since Enero 2024
 */

export interface CurrencyFormatOptions {
  allowNegative?: boolean;
  maxDecimals?: number;
  minDecimals?: number;
}

export class CurrencyFormatter {
  
  /**
   * Formatea un número para visualización con formato brasileño
   * @param value - Valor numérico
   * @param options - Opciones de formateo
   * @returns String formateado (ej: "1.234.567,89")
   */
  static formatForDisplay(value: number | string, options: CurrencyFormatOptions = {}): string {
    const opts = {
      allowNegative: true,
      maxDecimals: 2,
      minDecimals: 2,
      ...options
    };

    if (value === null || value === undefined || value === '') {
      return '0' + ',00'.substring(0, opts.minDecimals + 1);
    }

    let numValue = typeof value === 'string' ? this.parseFromString(value) : Number(value);
    
    if (isNaN(numValue)) {
      return '0' + ',00'.substring(0, opts.minDecimals + 1);
    }

    if (!opts.allowNegative && numValue < 0) {
      numValue = 0;
    }

    // Formatear con separadores brasileños
    const formatter = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: opts.minDecimals,
      maximumFractionDigits: opts.maxDecimals
    });

    return formatter.format(numValue);
  }

  /**
   * Convierte un string con formato brasileño a número
   * @param value - String con formato brasileño (ej: "1.234.567,89")
   * @returns Número decimal
   */
  static parseFromString(value: string): number {
    if (!value || typeof value !== 'string') return 0;

    // Remover espacios
    let cleanValue = value.trim();

    // Si está vacío después de limpiar, retornar 0
    if (cleanValue === '') return 0;

    // Manejar signo negativo
    const isNegative = cleanValue.startsWith('-');
    if (isNegative) {
      cleanValue = cleanValue.substring(1);
    }

    // Detectar si tiene formato brasileño (última coma es decimal)
    const lastCommaIndex = cleanValue.lastIndexOf(',');
    const lastDotIndex = cleanValue.lastIndexOf('.');
    
    let result = 0;

    if (lastCommaIndex > lastDotIndex) {
      // Formato brasileño: 1.234.567,89
      const integerPart = cleanValue.substring(0, lastCommaIndex).replace(/\./g, '');
      const decimalPart = cleanValue.substring(lastCommaIndex + 1);
      result = parseFloat(integerPart + '.' + decimalPart);
    } else if (lastDotIndex > lastCommaIndex) {
      // Formato americano: 1,234,567.89 - convertir a brasileño
      const integerPart = cleanValue.substring(0, lastDotIndex).replace(/,/g, '');
      const decimalPart = cleanValue.substring(lastDotIndex + 1);
      result = parseFloat(integerPart + '.' + decimalPart);
    } else if (lastCommaIndex !== -1) {
      // Solo coma: 1234,89
      const parts = cleanValue.split(',');
      result = parseFloat(parts[0] + '.' + parts[1]);
    } else if (lastDotIndex !== -1) {
      // Solo punto: 1234.89
      result = parseFloat(cleanValue);
    } else {
      // Solo números enteros
      result = parseFloat(cleanValue);
    }

    if (isNaN(result)) result = 0;
    if (isNegative) result = -result;

    return result;
  }

  /**
   * Convierte un número a string para envío al backend
   * @param value - Valor numérico
   * @returns String con formato americano para el backend (ej: "1234567.89")
   */
  static formatForBackend(value: number | string): number {
    const numValue = typeof value === 'string' ? this.parseFromString(value) : Number(value);
    return isNaN(numValue) ? 0 : Number(numValue.toFixed(2));
  }

  /**
   * Maneja la entrada de texto en tiempo real
   * @param inputElement - Elemento HTML input
   * @param options - Opciones de formateo
   */
  static handleInput(inputElement: HTMLInputElement, options: CurrencyFormatOptions = {}): void {
    const opts = {
      allowNegative: true,
      maxDecimals: 2,
      ...options
    };

    const initialValue = inputElement.value;
    const cursorPosition = inputElement.selectionStart;

    // Permitir solo dígitos, comas, puntos y signo negativo si está permitido
    let allowedChars = '0-9,.-';
    if (!opts.allowNegative) {
      allowedChars = '0-9,.';
    }
    
    const regex = new RegExp(`[^${allowedChars}]`, 'g');
    let value = initialValue.replace(regex, '');

    // Manejar signo negativo solo al inicio
    if (opts.allowNegative) {
      const negativeCount = (value.match(/-/g) || []).length;
      if (negativeCount > 1) {
        // Mantener solo el primer signo negativo si está al inicio
        value = value.replace(/-/g, '');
        if (initialValue.startsWith('-')) {
          value = '-' + value;
        }
      } else if (negativeCount === 1 && !value.startsWith('-')) {
        // Mover el signo negativo al inicio
        value = '-' + value.replace('-', '');
      }
    }

    // Limitar decimales
    const lastCommaIndex = value.lastIndexOf(',');
    if (lastCommaIndex !== -1) {
      const beforeComma = value.substring(0, lastCommaIndex);
      const afterComma = value.substring(lastCommaIndex + 1);
      
      // Remover comas adicionales antes de la última
      const cleanBeforeComma = beforeComma.replace(/,/g, '');
      
      // Limitar decimales
      const limitedDecimals = afterComma.substring(0, opts.maxDecimals);
      
      value = cleanBeforeComma + ',' + limitedDecimals;
    }

    // Actualizar el campo si cambió
    if (value !== initialValue) {
      inputElement.value = value;
      
      // Restaurar posición del cursor
      if (cursorPosition !== null) {
        const lengthDiff = value.length - initialValue.length;
        const newPosition = Math.min(Math.max(0, cursorPosition + lengthDiff), value.length);
        inputElement.setSelectionRange(newPosition, newPosition);
      }
    }
  }

  /**
   * Formatea un input al perder el foco (blur)
   * @param inputElement - Elemento HTML input
   * @param options - Opciones de formateo
   */
  static handleBlur(inputElement: HTMLInputElement, options: CurrencyFormatOptions = {}): void {
    const numValue = this.parseFromString(inputElement.value);
    inputElement.value = this.formatForDisplay(numValue, options);
  }

  /**
   * Prepara un input al recibir foco (focus) - remueve formateo para edición
   * @param inputElement - Elemento HTML input
   */
  static handleFocus(inputElement: HTMLInputElement): void {
    const numValue = this.parseFromString(inputElement.value);
    if (numValue === 0) {
      inputElement.value = '';
    } else {
      // Mostrar sin separadores de miles para facilitar edición
      const formatted = numValue.toFixed(2).replace('.', ',');
      inputElement.value = formatted;
    }
    
    // Seleccionar todo el texto
    inputElement.select();
  }
}

/**
 * Directiva personalizada para inputs de moneda
 * Uso: <input currencyInput [currencyOptions]="{maxDecimals: 2}">
 */
import { Directive, ElementRef, Input, OnInit, OnDestroy } from '@angular/core';

@Directive({
  selector: '[currencyInput]'
})
export class CurrencyInputDirective implements OnInit, OnDestroy {
  @Input() currencyOptions: CurrencyFormatOptions = {};

  private element: HTMLInputElement;

  constructor(private el: ElementRef) {
    this.element = el.nativeElement;
  }

  ngOnInit() {
    this.element.addEventListener('input', this.onInput.bind(this));
    this.element.addEventListener('blur', this.onBlur.bind(this));
    this.element.addEventListener('focus', this.onFocus.bind(this));
  }

  ngOnDestroy() {
    this.element.removeEventListener('input', this.onInput.bind(this));
    this.element.removeEventListener('blur', this.onBlur.bind(this));
    this.element.removeEventListener('focus', this.onFocus.bind(this));
  }

  private onInput(event: Event) {
    CurrencyFormatter.handleInput(this.element, this.currencyOptions);
  }

  private onBlur(event: Event) {
    CurrencyFormatter.handleBlur(this.element, this.currencyOptions);
  }

  private onFocus(event: Event) {
    CurrencyFormatter.handleFocus(this.element);
  }
} 